import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProjectStatus } from "@/api/types";
import { cn } from "@/lib/utils";

const STEPS = ["discovery", "design", "build", "live"] as const;

export function StatusTimeline({ status }: { status: ProjectStatus }) {
  const { t } = useTranslation();
  const currentIndex = STEPS.indexOf(status as (typeof STEPS)[number]);
  return (
    <ol aria-label={t("project.timeline")} className="flex w-full items-start">
      {STEPS.map((step, i) => {
        const done = currentIndex > i;
        const current = currentIndex === i;
        return (
          <li
            key={step}
            data-step={step}
            aria-current={current ? "step" : undefined}
            className="relative flex flex-1 flex-col items-center gap-2 text-center"
          >
            {i > 0 ? (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute right-1/2 top-3 h-0.5 w-full -translate-y-1/2",
                  currentIndex >= i ? "bg-secondary" : "bg-surface-sunken",
                )}
              />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                "relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2",
                done && "border-secondary bg-secondary text-fg-inverse",
                current && "border-danger bg-surface ring-4 ring-brand-subtle",
                !done && !current && "border-border-strong bg-surface",
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
              {current ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
            </span>
            <span className={cn("text-sm", current ? "font-semibold text-heading" : "text-fg-muted")}>
              {t(`project.status.${step}`)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
